// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import PackagesPage from './pages/PackagesPage';
import GenresPage from './pages/GenresPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DetailsPage from './pages/DetailsPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutForm from './components/OrderProcess/CheckoutForm';
import ConfirmationPage from './pages/ConfirmationPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminOrders from './pages/AdminOrders';
import ReviewPage from './pages/ReviewPage';
import EmailVerificationHandler from './components/EmailVerificationHandler';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import ActionHandler from './components/auth/ActionHandler';
import MothersDay from './pages/MothersDay';
import DiscountBridge from './components/DiscountBridge';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrderProvider, useOrder } from './contexts/OrderContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import './styles/globalStyleFix.css';

// Import Footer Pages
import AboutUsPage from './pages/AboutUsPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import TermsPrivacyPage from './pages/TermsPrivacyPage';

// Guest Order Route - allows both authenticated and unauthenticated users
function GuestOrderRoute({ children, requiredSteps = [] }) {
  const { currentOrder } = useOrder();
  const location = useLocation();

  const isStepCompleted = requiredSteps.every((step) => {
    switch (step) {
      case 'package':
        return currentOrder && currentOrder.package;
      case 'genres':
        return currentOrder && currentOrder.genres && currentOrder.genres.length > 0;
      case 'details':
        return currentOrder && currentOrder.details;
      case 'checkout':
        return currentOrder && currentOrder.progress && currentOrder.progress.checkout;
      default:
        return false;
    }
  });

  if (!isStepCompleted) {
    if (!currentOrder || !currentOrder.package) {
      return <Navigate to="/packages" state={{ from: location }} replace />;
    }
    if (!currentOrder.genres || currentOrder.genres.length === 0) {
      return <Navigate to="/genres" state={{ from: location }} replace />;
    }
    if (!currentOrder.details) {
      return <Navigate to="/details" state={{ from: location }} replace />;
    }
  }
  return children;
}

// Route Guard for final checkout page - requires authentication
function CheckoutProtectedRoute({ children, requiredSteps = [] }) {
  const { currentOrder } = useOrder();
  const location = useLocation();
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div className="spinner-container"><div className="spinner"></div></div>;
  }

  const isStepCompleted = requiredSteps.every((step) => {
    switch (step) {
      case 'package':
        return currentOrder && currentOrder.package;
      case 'genres':
        return currentOrder && currentOrder.genres && currentOrder.genres.length > 0;
      case 'details':
        return currentOrder && currentOrder.details;
      case 'checkout':
        return currentOrder && currentOrder.progress && currentOrder.progress.checkout;
      default:
        return false;
    }
  });

  if (!isStepCompleted) {
    if (!currentOrder || !currentOrder.package) {
      return <Navigate to="/packages" state={{ from: location }} replace />;
    }
    if (!currentOrder.genres || currentOrder.genres.length === 0) {
      return <Navigate to="/genres" state={{ from: location }} replace />;
    }
    if (!currentOrder.details) {
      return <Navigate to="/details" state={{ from: location }} replace />;
    }
  }

  return children;
}

// Auth-only route guard for routes like the dashboard
function AuthProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div className="spinner-container"><div className="spinner"></div></div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Add verification requirement to routes that need it
function VerifiedUserRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="spinner-container"><div className="spinner"></div></div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser.emailVerified) {
    return children;
  }

  return children;
}

// Admin Protected Route Component
function AdminRoute({ children }) {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { currentUser, loading: authLoading } = useAuth();

  console.log('AdminRoute - Current User:', currentUser);
  console.log('AdminRoute - Auth Loading:', authLoading);
  console.log('AdminRoute - Admin Loading:', adminLoading);
  console.log('AdminRoute - Is Admin:', isAdmin);

  if (authLoading || adminLoading) {
    return <div>Loading...</div>;
  }
  if (!currentUser || !isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function Layout() {
  const { currentUser } = useAuth();

  return (
    <>
      <Header />
      <DiscountBridge />
      {currentUser && !currentUser.emailVerified && (
        <div
          className="email-verification-banner-container"
          style={{
            marginTop: '15px',
            marginBottom: '15px',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'center',
            isolation: 'isolate',
          }}
        >
          <div style={{ width: '100%', maxWidth: '1200px', padding: '0 20px' }}>
            <EmailVerificationBanner />
          </div>
        </div>
      )}
      <Outlet />
      <Footer />
    </>
  );
}

function App() {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      <AuthProvider>
        <OrderProvider>
          <AdminProvider>
            <Router>
              <Routes>
                <Route path="/__/auth/action" element={<ActionHandler />} />
                <Route path="/verify-email" element={<EmailVerificationHandler />} />
                <Route path="/mothers-day" element={<MothersDay />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="packages" element={<PackagesPage />} />
                  <Route
                    path="genres"
                    element={
                      <GuestOrderRoute requiredSteps={['package']}>
                        <GenresPage />
                      </GuestOrderRoute>
                    }
                  />
                  <Route
                    path="details"
                    element={
                      <GuestOrderRoute requiredSteps={['package', 'genres']}>
                        <DetailsPage />
                      </GuestOrderRoute>
                    }
                  />
                  <Route
                    path="checkout"
                    element={
                      <GuestOrderRoute requiredSteps={['package', 'genres', 'details']}>
                        <CheckoutPage />
                      </GuestOrderRoute>
                    }
                  />
                  <Route
                    path="checkout/complete"
                    element={
                      <CheckoutProtectedRoute requiredSteps={['package', 'genres', 'details']}>
                        <CheckoutForm />
                      </CheckoutProtectedRoute>
                    }
                  />
                  <Route
                    path="confirmation"
                    element={
                      <AuthProtectedRoute>
                        <ConfirmationPage />
                      </AuthProtectedRoute>
                    }
                  />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route
                    path="dashboard"
                    element={
                      <AuthProtectedRoute>
                        <DashboardPage />
                      </AuthProtectedRoute>
                    }
                  />
                  <Route
                    path="review/:songId"
                    element={
                      <AuthProtectedRoute>
                        <ReviewPage />
                      </AuthProtectedRoute>
                    }
                  />
                  <Route path="about" element={<AboutUsPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="terms-privacy" element={<TermsPrivacyPage />} />
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/orders"
                    element={
                      <AdminRoute>
                        <AdminOrders />
                      </AdminRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Router>
          </AdminProvider>
        </OrderProvider>
      </AuthProvider>
    </div>
  );
}

export default App;